/**
 * Role-based data visibility permissions.
 * Controls which student data categories a staff member can see.
 * This is UI-level gating — not enforced by Firestore rules (which are doc-level only).
 */
export interface RolePermissions {
  currentGrades: boolean;
  attendance: boolean;
  behavior: boolean;
  interventions: boolean;
  assessments: boolean;
  transcript: boolean;
}

/**
 * Stored at `config/rolePermissions`.
 * Maps staff_type string (or "_default") to permissions.
 * Super-admin editable. Falls back to DEFAULT_ROLE_PERMISSIONS for any missing key.
 */
export type RolePermissionsConfig = Partial<Record<string, RolePermissions>>;

export const ALL: RolePermissions = {
  currentGrades: true,
  attendance: true,
  behavior: true,
  interventions: true,
  assessments: true,
  transcript: true,
};

const PARA: RolePermissions = {
  currentGrades: true,
  attendance: true,
  behavior: false,
  interventions: true,
  assessments: false,
  transcript: false,
};

/**
 * Hardcoded defaults — used when `config/rolePermissions` doc is absent or
 * doesn't specify a given staff_type. Super admins can override via Firestore.
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<string, RolePermissions> = {
  Teacher: ALL,
  Administrator: ALL,
  Counselor: ALL,
  Director: ALL,
  Librarian: ALL,
  Manager: ALL,
  Paraprofessional: PARA,
  Nurse: PARA,
  Substitute: PARA,
  Assistant: PARA,
  Secretary: PARA,
  Athletics: PARA,
  'Board Member': PARA,
  'Teaching Services': ALL,
  _default: PARA,
};

/**
 * Resolve the base visibility tier for a user.
 *
 * '_default' as staffType means "no staff record matched" (or the record has
 * no staff_type) — NOT a real match. Admins in that situation get full
 * visibility rather than silently landing on the para-level default tier.
 * (Bug 2026-07-31: the fallback checked `!matchedTier`, but the '_default'
 * tier always exists, so the fallback never fired.)
 */
export function resolveBaseTier(opts: {
  staffType: string;
  config: RolePermissionsConfig;
  isAdmin: boolean;
  impersonating: boolean;
}): { tier: RolePermissions; unmatchedAdminFallback: boolean } {
  const { staffType, config, isAdmin, impersonating } = opts;
  const matched =
    staffType !== '_default'
      ? (config[staffType] ?? DEFAULT_ROLE_PERMISSIONS[staffType])
      : undefined;
  const unmatchedAdminFallback = !matched && isAdmin && !impersonating;
  const tier =
    matched ??
    (unmatchedAdminFallback
      ? ALL
      : (config['_default'] ?? DEFAULT_ROLE_PERMISSIONS['_default']));
  return { tier, unmatchedAdminFallback };
}

export interface PermissionsPaths {
  'config/rolePermissions': RolePermissionsConfig;
}
